import { motion, AnimatePresence } from 'framer-motion';
import { X, ClipboardList, Timer, LayoutDashboard, CheckCircle, Award } from 'lucide-react';

interface CadreProgramIAModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CadreProgramIAModal({ isOpen, onClose }: CadreProgramIAModalProps) {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 rounded-2xl max-w-6xl w-full my-8 border border-purple-500/30 shadow-2xl shadow-purple-500/20"
          >
            {/* Header */}
            <div className="relative p-8 border-b border-purple-500/30">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl">
                    <ClipboardList className="w-10 h-10 text-white" />
                  </div>
                  <div>
                    <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
                      Cadre PROGRAM-IA
                    </h2>
                    <p className="text-gray-400 mt-1 text-lg">
                      Norme obligatoire pour tous les projets
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-8 space-y-8 max-h-[calc(100vh-300px)] overflow-y-auto">
              
              <div className="bg-gradient-to-r from-purple-600/20 via-pink-600/20 to-purple-600/20 rounded-xl p-8 border-2 border-purple-500/50">
                <h3 className="text-2xl font-bold text-center text-white mb-4">
                  Le Cadre PROGRAM-IA : Notre Norme
                </h3>
                <p className="text-gray-300 text-center text-lg">
                  Trois étapes obligatoires pour garantir la qualité et la rapidité de livraison de tous nos projets.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-xl p-6 border border-purple-500/30">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-purple-500/20 rounded-lg">
                      <ClipboardList className="w-8 h-8 text-purple-400" />
                    </div>
                    <h4 className="text-xl font-bold text-white">1. Cahier 1-page</h4>
                  </div>
                  <p className="text-gray-300 mb-4">
                    <strong className="text-white">Spécifications d'exigences 1-page signées</strong>
                  </p>
                  <p className="text-gray-300 text-sm">
                    Document concis et signé qui définit les besoins essentiels du projet.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-pink-500/10 to-rose-500/10 rounded-xl p-6 border border-pink-500/30">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-pink-500/20 rounded-lg">
                      <Timer className="w-8 h-8 text-pink-400" />
                    </div>
                    <h4 className="text-xl font-bold text-white">2. POC ≤ 48h</h4>
                  </div>
                  <p className="text-gray-300 mb-4">
                    <strong className="text-white">POC ≤ 48h</strong> (mock data, 5 écrans, Go/No-Go)
                  </p>
                  <p className="text-gray-300 text-sm">
                    Validation rapide du concept avec données mockées et 5 écrans clés. Décision Go/No-Go.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-rose-500/10 to-purple-500/10 rounded-xl p-6 border border-rose-500/30">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-rose-500/20 rounded-lg">
                      <LayoutDashboard className="w-8 h-8 text-rose-400" />
                    </div>
                    <h4 className="text-xl font-bold text-white">3. Overview in-app</h4>
                  </div>
                  <p className="text-gray-300 mb-4">
                    <strong className="text-white">Overview in-app obligatoire</strong>
                  </p>
                  <p className="text-gray-300 text-sm">
                    ROI/KPI, Sécurité NIST-style, IA/Éthique, README, Roadmap. Visible dans chaque app.
                  </p>
                </div>
              </div>

              <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 rounded-xl p-6 border border-indigo-500/30">
                <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                  <Award className="w-7 h-7 text-indigo-400" />
                  Règles Clés
                </h4>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                    <span><strong className="text-white">Pas de dev sans 1-page</strong> - Le cahier 1-page signé est obligatoire avant tout développement</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                    <span><strong className="text-white">POC en 48h sinon on re-cadre</strong> - Si le POC dépasse 48h, on re-cadre le projet</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                    <span><strong className="text-white">Overview visible dans chaque app</strong> - L'Overview in-app est obligatoire dans 100% des applications</span>
                  </li>
                </ul>
              </div>

            </div>

            {/* Footer */}
            <div className="p-6 border-t border-purple-500/30 bg-slate-900/50">
              <div className="flex items-center justify-end">
                <button
                  onClick={onClose}
                  className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all duration-200 font-semibold shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40 hover:scale-105"
                >
                  Fermer
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}












